import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useMergePerson, useMergePersonPreview, usePeopleSearch, usePerson } from '@/api/hooks'
import {
  ParticipationSummary,
  combineParticipation,
  useFullParticipation,
} from '@/components/people/person-participation'
import { parseApiErrorMessage } from '@/lib/api-errors'
import type { MergePeopleOutput, MergePeoplePreviewOutput, TPerson } from '@schemas'

type PreviewKey = 'email' | 'phone' | 'ahstcMember' | 'notes'

const PREVIEW_FIELDS: Array<{ key: PreviewKey; label: string }> = [
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'ahstcMember', label: 'AHSTC Member' },
  { key: 'notes', label: 'Notes' },
]

function formatValue(person: TPerson | null, key: PreviewKey) {
  if (!person) return '—'
  const value = person[key]
  if (key === 'ahstcMember') {
    return value ? 'Yes' : 'No'
  }
  if (typeof value === 'string' && value.trim().length > 0) return value
  return value ?? '—'
}

type ChangedField = MergePeoplePreviewOutput['keptChanges'][number]['field']
type BlockedReason = MergePeoplePreviewOutput['blockedShows'][number]['reason']

const CHANGE_LABELS: Record<ChangedField, string> = {
  email: 'Email',
  phone: 'Phone',
  notes: 'Notes',
  ahstcMember: 'AHSTC member',
  ahstcJudge: 'AHSTC judge',
  memberId: 'Member ID',
  membershipExpiresAt: 'Membership expires',
  judgeCertificationLevel: 'Judge certification',
  judgeStatus: 'Judge status',
  judgeTrainingDate: 'Judge training date',
  judgeDirectoryPhotoUrl: 'Judge directory photo',
}

const BLOCKED_REASONS: Record<BlockedReason, string> = {
  outgoing: 'is being handed over to another installation. Complete or cancel that handover first.',
  inactive: 'is now run by another installation, so its records cannot be changed here.',
  handover_prepared:
    'has a handover to this installation being prepared. Complete or withdraw it first.',
  not_owned: 'is not run by this installation.',
}

const REFUSALS: Record<NonNullable<MergePeoplePreviewOutput['refusal']>, string> = {
  linked_account:
    'A sign-in account is linked to one of these people. An administrator must unlink it before they can be merged.',
  credential_direction:
    'The duplicate holds a credential number. Keep the person with the credential number as the target instead.',
}

function formatChange(field: ChangedField, value: string | boolean | null) {
  if (value === null || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return field === 'membershipExpiresAt' || field === 'judgeTrainingDate'
    ? value.slice(0, 10)
    : value
}

const unmanaged = (count: number) => `${count} ${count === 1 ? 'show' : 'shows'} you do not manage`

/** The server's account of the merge. The sheet never works out for itself which shows it touches. */
function MergeReview({
  targetName,
  preview,
  status,
  retry,
}: {
  targetName: string
  preview: MergePeoplePreviewOutput | undefined
  status: 'loading' | 'failed' | 'ready'
  retry: () => void
}) {
  const title = 'What this merge changes'
  if (status === 'failed' || (status === 'ready' && !preview)) {
    return (
      <section aria-label={title} className="space-y-2 rounded-md border p-3 text-sm">
        <h4 className="font-medium">{title}</h4>
        <div role="alert" className="space-y-2">
          <p>This merge could not be checked.</p>
          <Button type="button" variant="outline" size="sm" onClick={retry}>
            Retry check
          </Button>
        </div>
      </section>
    )
  }
  if (status === 'loading' || !preview) {
    return (
      <section aria-label={title} className="space-y-2 rounded-md border p-3 text-sm">
        <h4 className="font-medium">{title}</h4>
        <p role="status" className="text-muted-foreground">
          Checking this merge…
        </p>
      </section>
    )
  }
  const readOnly = preview.readOnlyShows.length + preview.outsideScope.readOnly
  // A server older than the class-conflict check omits both fields.
  const classConflicts = preview.classConflicts ?? []
  const hiddenClassConflicts = preview.outsideScope.classConflicts ?? 0
  return (
    <section aria-label={title} className="space-y-3 rounded-md border p-3 text-sm">
      <h4 className="font-medium">{title}</h4>
      {preview.keptChanges.length ? (
        <ul className="space-y-1">
          {preview.keptChanges.map(change => (
            <li key={change.field}>
              <span className="font-medium">{CHANGE_LABELS[change.field]}:</span>{' '}
              {formatChange(change.field, change.from)} → {formatChange(change.field, change.to)}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-muted-foreground">No details of {targetName} change.</p>
      )}
      {preview.contactCorrectionFields && (
        <div className="space-y-1">
          <p>Contact correction protection</p>
          {(['retained', 'adopted', 'released'] as const).map(
            kind =>
              preview.contactCorrectionFields![kind].length > 0 && (
                <p key={kind}>
                  {kind}: {preview.contactCorrectionFields![kind].join(', ')}
                </p>
              )
          )}
        </div>
      )}
      {preview.mergeable && readOnly > 0 ? (
        <div className="space-y-1">
          <p>
            These shows are read-only here. After the merge, their entrant, staff and judge records
            for these people all name {targetName}. Nothing else in them changes, and they stay
            read-only.
          </p>
          <ul className="list-disc pl-5">
            {preview.readOnlyShows.map(show => (
              <li key={show.id}>{show.name}</li>
            ))}
            {preview.outsideScope.readOnly > 0 ? (
              <li>{unmanaged(preview.outsideScope.readOnly)}</li>
            ) : null}
          </ul>
        </div>
      ) : null}
      {preview.mergeable && classConflicts.length + hiddenClassConflicts > 0 ? (
        <div role="status" className="space-y-1 rounded-md bg-amber-50 p-2 text-amber-900">
          <p className="font-medium">Both people have an active entry in the same class</p>
          <p>
            A person can have only one active entry per class. After the merge, mark one of each
            pair Not Entered.
          </p>
          <ul className="list-disc pl-5">
            {classConflicts.map(conflict => (
              <li key={`${conflict.show.id}:${conflict.entryCodes.join()}`}>
                {conflict.show.name}, {conflict.divisionName}, {conflict.className}: Exhibit IDs{' '}
                {conflict.entryCodes.join(', ')}
              </li>
            ))}
            {hiddenClassConflicts > 0 ? (
              <li>
                {hiddenClassConflicts} {hiddenClassConflicts === 1 ? 'class' : 'classes'} in shows
                you do not manage
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}
      {preview.mergeable && preview.keptDetailsLocked ? (
        <p className="text-muted-foreground">
          {targetName} is part of a show that is being handed over, so their details cannot be
          changed until that handover is complete or cancelled.
        </p>
      ) : null}
      {!preview.mergeable ? (
        <div role="alert" className="space-y-1 rounded-md bg-destructive/5 p-2">
          <p className="font-medium">These people cannot be merged yet.</p>
          <ul className="list-disc pl-5">
            {preview.refusal ? <li>{REFUSALS[preview.refusal]}</li> : null}
            {preview.blockedShows.map(show => (
              <li key={show.id}>
                {show.name} {BLOCKED_REASONS[show.reason]}
              </li>
            ))}
            {preview.outsideScope.blocked > 0 ? (
              <li>
                {unmanaged(preview.outsideScope.blocked)}{' '}
                {preview.outsideScope.blocked === 1 ? 'prevents' : 'prevent'} this merge. Ask an
                administrator to review it.
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </section>
  )
}

export type MergePeopleSheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  targetPerson: Pick<TPerson, 'id' | 'name'> | null
  onMerged?: (result: MergePeopleOutput) => void
}

type MergeStrategy = 'preserve' | 'overwrite'

export function MergePeopleSheet({
  open,
  onOpenChange,
  targetPerson: targetIdentity,
  onMerged,
}: MergePeopleSheetProps) {
  const targetQuery = usePerson(open ? targetIdentity?.id : null)
  // A background refetch keeps the loaded person; a first load or a retry after failure hides it.
  const targetLoading = targetQuery.isFetching && (!targetQuery.data || targetQuery.isError)
  const targetPerson = targetLoading || targetQuery.isError ? undefined : targetQuery.data
  const [searchTerm, setSearchTerm] = useState('')
  const deferredTerm = useDeferredValue(searchTerm)
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null)
  const [strategy, setStrategy] = useState<MergeStrategy>('preserve')

  const mergePerson = useMergePerson()

  useEffect(() => {
    if (!open) {
      setSearchTerm('')
      setSelectedSourceId(null)
      setStrategy('preserve')
    }
  }, [open])

  useEffect(() => {
    setSelectedSourceId(null)
  }, [targetIdentity?.id])

  const trimmed = deferredTerm.trim()
  const searchEnabled = open && trimmed.length >= 2

  const peopleQuery = usePeopleSearch(
    { q: searchEnabled ? trimmed : undefined, pageSize: 20 },
    { enabled: searchEnabled }
  )

  const candidateSources = useMemo(() => {
    if (!searchEnabled) return []
    return (peopleQuery.data?.people ?? []).filter(
      person => person.id && person.id !== targetIdentity?.id
    )
  }, [peopleQuery.data, searchEnabled, targetIdentity?.id])

  const selectedSource = useMemo(() => {
    if (!selectedSourceId) return null
    return candidateSources.find(person => person.id === selectedSourceId) ?? null
  }, [candidateSources, selectedSourceId])

  const hasConflict = useMemo(() => {
    if (!targetPerson || !selectedSource) return false
    return PREVIEW_FIELDS.some(
      field => formatValue(targetPerson, field.key) !== formatValue(selectedSource, field.key)
    )
  }, [selectedSource, targetPerson])

  // Both histories come from the same global API; identities stay separate until the merge.
  const retained = useFullParticipation(open ? targetPerson?.id : null)
  const duplicate = useFullParticipation(open ? selectedSource?.id : null)
  const reviewReady = retained.status === 'complete' && duplicate.status === 'complete'
  const combined = useMemo(
    () => combineParticipation(retained.shows, duplicate.shows),
    [retained.shows, duplicate.shows]
  )
  // The server decides whether these people can be merged and which shows the merge touches.
  const preview = useMergePersonPreview(
    open ? targetPerson?.id : null,
    open ? selectedSource?.id : null,
    strategy
  )
  const previewStatus = preview.isError ? 'failed' : preview.isFetching ? 'loading' : 'ready'
  const plan = previewStatus === 'ready' ? preview.data : undefined

  const handleConfirm = async () => {
    if (!targetPerson?.id || !selectedSource?.id) {
      toast.error('Select a duplicate person to merge')
      return
    }

    try {
      const result = await mergePerson.mutateAsync({
        personId: targetPerson.id,
        payload: {
          sourcePersonId: selectedSource.id,
          strategy,
          // The server refuses the merge if either history changed after this review.
          review:
            retained.reviewToken && duplicate.reviewToken
              ? { target: retained.reviewToken, source: duplicate.reviewToken }
              : undefined,
          // And if what it would change is no longer what the review below showed.
          planToken: plan?.planToken,
        },
      })
      toast.success('People merged', {
        description: `${selectedSource.name ?? 'Duplicate'} merged into ${targetPerson.name ?? 'target'}.`,
      })
      onMerged?.(result)
      onOpenChange(false)
    } catch (error) {
      toast.error('Unable to merge people', {
        description: parseApiErrorMessage(error),
      })
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>Merge duplicate person</SheetTitle>
          <SheetDescription>
            Consolidate duplicate records. Choose the target and duplicate, then decide how to
            resolve conflicting fields.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-4 pb-6 pt-2 min-h-0">
          <div className="flex flex-col gap-6">
            <div className="space-y-2">
              <Label>Target person</Label>
              <div className="border rounded-md p-3 space-y-1 text-sm">
                {targetLoading ? (
                  <p role="status">Loading stored person…</p>
                ) : targetQuery.isError ? (
                  <div role="alert">
                    <p>Unable to load the stored person. Try again before merging.</p>
                    <Button onClick={() => void targetQuery.refetch()}>Retry person</Button>
                  </div>
                ) : targetPerson ? (
                  <>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{targetPerson.name}</p>
                      {targetPerson.ahstcMember && <Badge variant="secondary">AHSTC</Badge>}
                    </div>
                    <p className="text-muted-foreground">
                      {[targetPerson.email, targetPerson.phone].filter(Boolean).join(' • ') ||
                        'No contact info'}
                    </p>
                  </>
                ) : (
                  <p className="text-muted-foreground">
                    Select a person from the directory to merge into.
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Search for duplicate</Label>
              <Input
                value={searchTerm}
                onChange={event => setSearchTerm(event.target.value)}
                placeholder="Search by name, email, phone, or entrant code"
              />
              {!searchEnabled ? (
                <p className="text-xs text-muted-foreground">
                  Type at least two characters to search for duplicates.
                </p>
              ) : peopleQuery.isFetching ? (
                <p className="text-xs text-muted-foreground">Searching…</p>
              ) : candidateSources.length === 0 ? (
                <p className="text-xs text-muted-foreground">No potential duplicates found.</p>
              ) : (
                <ul className="divide-y rounded-md border">
                  {candidateSources.map(person => (
                    <li key={person.id} className="flex items-center justify-between gap-3 p-3">
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{person.name}</p>
                          {person.ahstcMember && <Badge variant="secondary">AHSTC</Badge>}
                        </div>
                        <p className="text-muted-foreground">
                          {[person.email, person.phone].filter(Boolean).join(' • ') ||
                            'No contact info'}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant={selectedSourceId === person.id ? 'default' : 'outline'}
                        onClick={() => setSelectedSourceId(person.id ?? null)}
                      >
                        {selectedSourceId === person.id ? 'Selected' : 'Select'}
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {targetPerson && selectedSource && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Conflict preview</Label>
                  {hasConflict ? (
                    <Badge variant="destructive">Conflicts detected</Badge>
                  ) : (
                    <Badge variant="outline">No conflicts</Badge>
                  )}
                </div>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="w-1/3">Field</TableHead>
                        <TableHead>Target value</TableHead>
                        <TableHead>Duplicate value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {PREVIEW_FIELDS.map(field => {
                        const targetValue = formatValue(targetPerson ?? null, field.key)
                        const sourceValue = formatValue(selectedSource, field.key)
                        const conflict = targetValue !== sourceValue
                        return (
                          <TableRow
                            key={field.key}
                            className={conflict ? 'bg-destructive/5' : undefined}
                          >
                            <TableCell className="font-medium">{field.label}</TableCell>
                            <TableCell>{targetValue}</TableCell>
                            <TableCell>{sourceValue}</TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {targetPerson ? (
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label>Participation</Label>
                  <p className="text-xs text-muted-foreground">
                    Compare both histories before merging. Entrant records and their entries are
                    kept and move to the target person.
                  </p>
                </div>
                <ParticipationSummary
                  title="Target person"
                  description={targetPerson.name}
                  participation={retained}
                />
                {selectedSource ? (
                  <ParticipationSummary
                    title="Duplicate person"
                    description={selectedSource.name}
                    participation={duplicate}
                  />
                ) : null}
                {selectedSource && reviewReady ? (
                  <ParticipationSummary
                    title="Combined after merge"
                    description="In shows you can access. A show both took part in is counted once."
                    participation={{ shows: combined, status: 'complete' }}
                  />
                ) : null}
              </div>
            ) : null}

            <div className="space-y-2">
              <Label>Conflict strategy</Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant={strategy === 'preserve' ? 'default' : 'outline'}
                  onClick={() => setStrategy('preserve')}
                  disabled={mergePerson.isPending}
                >
                  Preserve target
                </Button>
                <Button
                  type="button"
                  variant={strategy === 'overwrite' ? 'default' : 'outline'}
                  onClick={() => setStrategy('overwrite')}
                  disabled={mergePerson.isPending}
                >
                  Overwrite with duplicate
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Preserve target keeps existing contact details unless missing. Overwrite copies
                values from the duplicate person when conflicts exist.
              </p>
            </div>

            {targetPerson && selectedSource ? (
              <MergeReview
                targetName={targetPerson.name}
                preview={preview.data}
                status={previewStatus}
                retry={() => void preview.refetch()}
              />
            ) : null}
          </div>
        </div>

        <Separator className="my-4" />

        <SheetFooter className="flex w-full gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mergePerson.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={
              !targetPerson ||
              !selectedSource ||
              !reviewReady ||
              !plan?.mergeable ||
              mergePerson.isPending
            }
          >
            {mergePerson.isPending ? 'Merging…' : 'Merge selected person'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
